import { useState, useCallback } from 'react';
import { playbookApi } from '../api/playbookApi';
import type {
  AnalyticsOverview,
  StatusDwellTime,
  GuardFailureRate,
  ActionErrorRate,
  VersionComparisonItem,
  AnalyticsDateRange,
} from '../types/playbook.types';

export function usePlaybookAnalytics(playbookId: string | null, slug?: string | null) {
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [bottlenecks, setBottlenecks] = useState<StatusDwellTime[]>([]);
  const [guardFailures, setGuardFailures] = useState<GuardFailureRate[]>([]);
  const [actionErrors, setActionErrors] = useState<ActionErrorRate[]>([]);
  const [versionComparison, setVersionComparison] = useState<VersionComparisonItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAll = useCallback(async (dateRange?: AnalyticsDateRange) => {
    if (!playbookId) return;
    setLoading(true);
    setError(null);
    try {
      const [ovRes, bnRes, gfRes, aeRes] = await Promise.allSettled([
        playbookApi.getAnalyticsOverview(playbookId, dateRange),
        playbookApi.getBottlenecks(playbookId, dateRange),
        playbookApi.getGuardFailures(playbookId, dateRange),
        playbookApi.getActionErrors(playbookId, dateRange),
      ]);

      if (ovRes.status === 'fulfilled') {
        const raw = ovRes.value as any;
        // Normalize: API may return { completion, triggers, slaCompliance } at top level or nested under .data / .overview
        const ov = raw?.overview ?? raw?.data ?? raw;
        setOverview({
          completion: ov?.completion ?? ov?.completionMetrics ?? { totalInstances: 0, completedCount: 0, inProgressCount: 0, stuckCount: 0, avgCompletionHours: 0 },
          triggers: ov?.triggers ?? ov?.triggerDistribution ?? { total: 0, byType: {}, percentages: {} },
          slaCompliance: ov?.slaCompliance ?? [],
        });
      }
      if (bnRes.status === 'fulfilled') {
        const d = bnRes.value as any;
        setBottlenecks(Array.isArray(d) ? d : d.bottlenecks || []);
      }
      if (gfRes.status === 'fulfilled') {
        const d = gfRes.value as any;
        setGuardFailures(Array.isArray(d) ? d : d.guardFailures || []);
      }
      if (aeRes.status === 'fulfilled') {
        const d = aeRes.value as any;
        setActionErrors(Array.isArray(d) ? d : d.actionErrors || []);
      }

      // Version comparison (separate — uses slug)
      if (slug) {
        try {
          const vcRes = await playbookApi.getVersionComparison(slug);
          const vd = vcRes as any;
          setVersionComparison(Array.isArray(vd) ? vd : vd.versions || []);
        } catch { /* silent — analytics might not be available yet */ }
      }
    } catch (err) {
      console.error('[usePlaybookAnalytics] Failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [playbookId, slug]);

  return {
    overview, bottlenecks, guardFailures, actionErrors, versionComparison,
    loading, error, loadAll,
  };
}
